// Sales-Nav-style boolean operator parser for Title and Keyword fields.
//
// Lets power users express compound criteria the regular chip pickers
// can't ("(staff AND engineer) OR (senior AND infrastructure)") and
// translates the parsed AST into Crustdata's `group_filter` shape so
// the same filter pipeline at people_search_api.py handles it.
//
// Supported grammar:
//   expr   := orTerm
//   orTerm := andTerm (OR andTerm)*
//   andTerm:= atom (AND atom)*
//   atom   := '(' expr ')' | WORD | QUOTED_WORD
//
// Notes:
// - Keywords AND / OR are case-insensitive but must be uppercase in
//   the canonical form we recommend in placeholder text.
// - Quoted strings ("head of growth") are treated as single terms so
//   spaces inside quotes don't tokenise into multiple terms.
// - NOT is intentionally not supported in v1. The negation use case
//   is handled by the existing exclude-chip UI; bolting NOT into
//   the parser would require DeMorgan flattening for the Crustdata
//   wire format, which is more surface area than the spec warrants.

import { FilterCompileError } from '@/utils/filter-compile-error.util';

// ─── AST ────────────────────────────────────────────────────────────

export type BooleanNode =
  | { kind: 'term'; value: string }
  | { kind: 'and'; children: BooleanNode[] }
  | { kind: 'or'; children: BooleanNode[] };

// ─── Tokenizer ──────────────────────────────────────────────────────

type Token =
  | { type: 'word'; value: string }
  | { type: 'lparen' }
  | { type: 'rparen' }
  | { type: 'and' }
  | { type: 'or' };

function tokenize(input: string): Token[] {
  const tokens: Token[] = [];
  let i = 0;
  while (i < input.length) {
    const ch = input[i];
    if (ch === ' ' || ch === '\t' || ch === '\n') {
      i++;
      continue;
    }
    if (ch === '(') {
      tokens.push({ type: 'lparen' });
      i++;
      continue;
    }
    if (ch === ')') {
      tokens.push({ type: 'rparen' });
      i++;
      continue;
    }
    if (ch === '"' || ch === '“' || ch === '”') {
      let j = i + 1;
      while (
        j < input.length &&
        input[j] !== '"' &&
        input[j] !== '“' &&
        input[j] !== '”'
      ) {
        j++;
      }
      // An unterminated quote used to swallow the rest of the line into one
      // term — a different query from the one on screen, accepted as valid.
      if (j >= input.length) {
        throw new BooleanQueryError('Unterminated quote.');
      }
      const value = input.slice(i + 1, j).trim();
      if (value) tokens.push({ type: 'word', value });
      i = j + 1;
      continue;
    }
    // Read a bareword up to the next whitespace, paren, or quote.
    let j = i;
    while (
      j < input.length &&
      input[j] !== ' ' &&
      input[j] !== '\t' &&
      input[j] !== '\n' &&
      input[j] !== '(' &&
      input[j] !== ')' &&
      input[j] !== '"'
    ) {
      j++;
    }
    const word = input.slice(i, j);
    const upper = word.toUpperCase();
    if (upper === 'AND') tokens.push({ type: 'and' });
    else if (upper === 'OR') tokens.push({ type: 'or' });
    else if (word) tokens.push({ type: 'word', value: word });
    i = j;
  }
  return tokens;
}

// ─── Parser ─────────────────────────────────────────────────────────

class Parser {
  private pos = 0;
  constructor(private tokens: Token[]) {}

  parse(): BooleanNode | null {
    if (this.tokens.length === 0) return null;
    const node = this.parseOr();
    if (this.pos !== this.tokens.length) {
      throw new BooleanQueryError(`Unexpected token at position ${this.pos}.`);
    }
    return node;
  }

  private parseOr(): BooleanNode {
    const left = this.parseAnd();
    if (this.peek()?.type !== 'or') return left;
    const children: BooleanNode[] = [left];
    while (this.peek()?.type === 'or') {
      this.consume();
      children.push(this.parseAnd());
    }
    return { kind: 'or', children };
  }

  private parseAnd(): BooleanNode {
    const left = this.parseAtom();
    if (this.peek()?.type !== 'and') return left;
    const children: BooleanNode[] = [left];
    while (this.peek()?.type === 'and') {
      this.consume();
      children.push(this.parseAtom());
    }
    return { kind: 'and', children };
  }

  private parseAtom(): BooleanNode {
    const t = this.peek();
    if (!t) throw new BooleanQueryError('Unexpected end of input.');
    if (t.type === 'lparen') {
      this.consume();
      const node = this.parseOr();
      const close = this.peek();
      if (close?.type !== 'rparen') {
        throw new BooleanQueryError('Missing closing parenthesis.');
      }
      this.consume();
      return node;
    }
    if (t.type === 'word') {
      this.consume();
      return { kind: 'term', value: t.value };
    }
    throw new BooleanQueryError(`Unexpected token: ${t.type}`);
  }

  private peek(): Token | undefined {
    return this.tokens[this.pos];
  }
  private consume(): Token | undefined {
    return this.tokens[this.pos++];
  }
}

export class BooleanQueryError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'BooleanQueryError';
  }
}

/**
 * Parse a boolean query like `(staff AND engineer) OR senior` into
 * a small AST. Returns null when the input has no meaningful terms
 * (so callers can short-circuit). Throws BooleanQueryError on
 * malformed input.
 */
export function parseBooleanQuery(input: string): BooleanNode | null {
  const trimmed = input.trim();
  if (!trimmed) return null;
  const tokens = tokenize(trimmed);
  if (tokens.length === 0) return null;
  return new Parser(tokens).parse();
}

/**
 * Parse for a COMPILER, not the UI: malformed input raises the shared
 * ``FilterCompileError`` so the dispatch boundary can refuse the whole request
 * rather than broaden it. Empty input is valid and returns null.
 */
export function parseBooleanQueryOrCompileError(
  input: string,
  field: string,
  label: string
): BooleanNode | null {
  try {
    return parseBooleanQuery(input);
  } catch (e) {
    throw new FilterCompileError(
      field,
      label,
      e instanceof BooleanQueryError ? e.message : 'Could not parse this query.'
    );
  }
}

// ─── Translation to Crustdata filter ────────────────────────────────

export type CrustdataLeaf = {
  column: string;
  type: string;
  value: unknown;
};

export type CrustdataGroupFilter = {
  type: 'group_filter';
  op: 'and' | 'or';
  filters: Array<CrustdataLeaf | CrustdataGroupFilter>;
};

/**
 * Translate a parsed boolean AST into the `group_filter` shape that
 * Crustdata's v1 persondb parser accepts. ``columns`` is the list of
 * fields each term must match against — a single-element list means
 * "term contains value on that one column", a multi-element list
 * means "term contains value on ANY of these columns" (used for the
 * Keyword field which spans headline + summary).
 */
export function booleanQueryToCrustdataFilter(
  node: BooleanNode,
  columns: string[],
  // ``[.]`` is Crustdata's native SUBSTRING operator, and substring is what a
  // Boolean term means: ``vp AND sales`` has to match "VP of Sales".
  //
  // The previous default of ``contains`` was a legacy operator the backend
  // rewrites (people.py ``_normalize_filter_type``) into ``multi_value_filter``
  // -> exact-list ``in``, so every Boolean term silently became an EXACT match
  // and "VP of Sales" could not satisfy ``vp``. ``(.)`` and ``[.]`` are the two
  // operators that backend passes through untouched.
  matchType: string = '[.]'
): CrustdataLeaf | CrustdataGroupFilter | null {
  if (columns.length === 0) return null;

  if (node.kind === 'term') {
    if (columns.length === 1) {
      return { column: columns[0], type: matchType, value: node.value };
    }
    return {
      type: 'group_filter',
      op: 'or',
      filters: columns.map((c) => ({
        column: c,
        type: matchType,
        value: node.value,
      })),
    };
  }

  const children = node.children
    .map((c) => booleanQueryToCrustdataFilter(c, columns, matchType))
    .filter((c): c is CrustdataLeaf | CrustdataGroupFilter => c !== null);

  if (children.length === 0) return null;
  if (children.length === 1) return children[0];

  return {
    type: 'group_filter',
    op: node.kind === 'and' ? 'and' : 'or',
    filters: children,
  };
}

/**
 * Validate the input string without keeping the parsed result. Used
 * by the UI to render a green-check / red-error hint under the input
 * before the search fires. Returns the error message or null on
 * success / empty input.
 */
export function validateBooleanQuery(input: string): string | null {
  try {
    parseBooleanQuery(input);
    return null;
  } catch (e) {
    if (e instanceof BooleanQueryError) return e.message;
    return 'Could not parse boolean query.';
  }
}
